export type IPVersion = 'ipv4' | 'ipv6' | 'both';

export interface GeneratedIP {
    ip: string;
    version: 'IPv4' | 'IPv6';
    type: string;
}

export interface IPGeneratorOptions {
    count: number;
    version: IPVersion;
    type?: 'public' | 'private' | 'any';
}

// Private IP ranges for IPv4
const PRIVATE_IPV4_RANGES = [
    { start: [10, 0, 0, 0], end: [10, 255, 255, 255], name: 'Class A Private' },
    { start: [172, 16, 0, 0], end: [172, 31, 255, 255], name: 'Class B Private' },
    { start: [192, 168, 0, 0], end: [192, 168, 255, 255], name: 'Class C Private' },
];

function randomInt(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

function generateRandomIPv4(isPrivate: boolean = false): GeneratedIP {
    let octets: number[];
    let type: string;

    if (isPrivate) {
        const range = PRIVATE_IPV4_RANGES[randomInt(0, PRIVATE_IPV4_RANGES.length - 1)];
        octets = [
            randomInt(range.start[0], range.end[0]),
            randomInt(range.start[1], range.end[1]),
            randomInt(range.start[2], range.end[2]),
            randomInt(range.start[3], range.end[3]),
        ];
        type = range.name;
    } else {
        // Generate public IP (avoid private, loopback, multicast ranges)
        let valid = false;
        octets = [0, 0, 0, 0];
        
        while (!valid) {
            octets = [
                randomInt(1, 223),
                randomInt(0, 255),
                randomInt(0, 255),
                randomInt(1, 254),
            ];
            
            // Check if it's not in private ranges
            const isPrivateRange = 
                (octets[0] === 10) ||
                (octets[0] === 172 && octets[1] >= 16 && octets[1] <= 31) ||
                (octets[0] === 192 && octets[1] === 168) ||
                (octets[0] === 127) || // Loopback
                (octets[0] >= 224); // Multicast/Reserved
            
            valid = !isPrivateRange;
        }
        type = 'Public';
    }

    return {
        ip: octets.join('.'),
        version: 'IPv4',
        type,
    };
}

function generateRandomIPv6(): GeneratedIP {
    const groups: string[] = [];
    
    for (let i = 0; i < 8; i++) {
        const group = randomInt(0, 65535).toString(16).padStart(4, '0');
        groups.push(group);
    }

    return {
        ip: groups.join(':'),
        version: 'IPv6',
        type: 'Global Unicast',
    };
}

export function generateIPs(options: IPGeneratorOptions): GeneratedIP[] {
    const { count, version, type = 'any' } = options;
    const results: GeneratedIP[] = [];

    for (let i = 0; i < count; i++) {
        let ip: GeneratedIP;

        if (version === 'ipv4') {
            const isPrivate = type === 'private' || (type === 'any' && Math.random() > 0.5);
            ip = generateRandomIPv4(isPrivate);
        } else if (version === 'ipv6') {
            ip = generateRandomIPv6();
        } else {
            // Both - alternate or random
            if (Math.random() > 0.5) {
                const isPrivate = type === 'private' || (type === 'any' && Math.random() > 0.5);
                ip = generateRandomIPv4(isPrivate);
            } else {
                ip = generateRandomIPv6();
            }
        }

        results.push(ip);
    }

    return results;
}

export const IP_VERSIONS = [
    { code: 'ipv4', name: 'IPv4' },
    { code: 'ipv6', name: 'IPv6' },
    { code: 'both', name: 'IPv4 & IPv6' },
];

export const IP_TYPES = [
    { code: 'any', name: 'Any' },
    { code: 'public', name: 'Public' },
    { code: 'private', name: 'Private (IPv4 only)' },
];
